import { t, Trans } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { useWindowDimensions } from "react-native";

import { Button } from "@whoosh/design-system/src/components/controls";
import { Avatar } from "@whoosh/design-system/src/components/display/Avatar";
import {
  HStack,
  Pressable,
  Text,
} from "@whoosh/design-system/src/components/primitive";
import { theme } from "@whoosh/design-system/src/theme/restyle/theme";

export type TopbarUserContentProps = {
  isSignedIn: boolean;
  isMember: boolean;
  firstName: string | null;
  lastName: string | null;
  profileImagePublicId: string | undefined;
  onSignIn: () => void;
  onProfilePress: () => void;
};

export const TopbarUserContent = ({
  isSignedIn,
  isMember,
  firstName,
  lastName,
  profileImagePublicId,
  onSignIn,
  onProfilePress,
}: TopbarUserContentProps) => {
  useLingui();

  const { width } = useWindowDimensions();
  const isAboveMobile = width >= theme.breakpoints.sm;
  const displayName = [firstName, lastName].filter(Boolean).join(" ");
  const shouldShowProfile =
    isMember || Boolean(displayName || profileImagePublicId);

  if (!isSignedIn) {
    return (
      <Button
        variant="outline"
        size="tiny"
        onPress={onSignIn}
        testID="topbar-sign-in"
        accessibilityLabel={t`Sign In`}
      >
        <Trans>Sign In</Trans>
      </Button>
    );
  }

  return (
    <HStack alignItems="center" gap={2} flexShrink={0}>
      {isMember ? null : (
        <Button
          variant="outline"
          size="tiny"
          onPress={onSignIn}
          testID="topbar-join-club"
          accessibilityLabel={t`Join club`}
        >
          <Trans>Join Club</Trans>
        </Button>
      )}
      {shouldShowProfile ? (
        <Pressable
          onPress={onProfilePress}
          testID="topbar-user-content"
          style={{ flexShrink: 1, minWidth: 0 }}
        >
          <HStack alignItems="center" gap={2} flexShrink={1} minWidth={0}>
            {isAboveMobile && displayName ? (
              <Text
                variant="mainBaseMedium"
                color="text-primary"
                numberOfLines={1}
                testID="topbar-user-name"
                flexShrink={1}
              >
                {displayName}
              </Text>
            ) : null}
            <Avatar
              profileImagePublicId={profileImagePublicId}
              firstName={firstName ?? undefined}
              lastName={lastName ?? undefined}
              size="xs"
              type="member"
              testID="topbar-user-avatar"
            />
          </HStack>
        </Pressable>
      ) : null}
    </HStack>
  );
};
